import { useTranslation } from 'react-i18next'
import Button from '../../components/Button'
import './index.scss'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import UserContext from '../../contexts/UserContext/context'
import Icon from '../../components/Icon'
import { ICON } from '../../components/Icon/data'
import { COLOR } from '../../data/variables'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { REPORTS } from './Data/data'
import Table from '../../components/Table'
import ReportNavbar from './ReportNavbar'
import Input from '../../components/Input'
import { useQuery } from 'react-query'
import { getMobileList } from '../../services/calls/mobiles'
import { DaterangeValue } from '../../components/Input/Daterange'
import { useFormat } from '../../hooks/useFormat'
import { updateSidebar } from '../../contexts/SidebarContext/actions'
import { initial_sidebar } from '../../contexts/SidebarContext/reducer'
import HighchartsReact from 'highcharts-react-official'
import * as Highcharts from 'highcharts'
import 'highcharts/highcharts-more'
import { useHighcharts } from '../../hooks/useHighcharts'
import LoadingSpinner from '../../components/LoadingSpinner'
import Infobox from '../../components/Infobox'
import { DYNAMIC_DATA } from './Data/dynamicData'
import { isValue } from '../../services/utils/misc'
import { DateTime } from 'luxon'
import { getFleets } from '../../services/calls/fleet'

type Report = {
  dynamicType?: 'year' | 'week' | 'month'
  showHeader?: boolean
  dynamicParams?: Record<string, any>
  fetchOnLoad?: boolean
  tableTitle?: string
}

const DEFAULT_FILE_TYPE = 'both'
const DEFAULT_SPEED_OVER = 10
const DEFAULT_WEEK_RANGE = DateTime.now().set({ weekday: 1 }).minus({ days: 7 }).toISODate()

const Reports = memo(({
  dynamicType, showHeader = true, dynamicParams = {}, fetchOnLoad = false, tableTitle
}: Report) => {
  const { t } = useTranslation()
  const tableRef = useRef()

  const { user } = useContext(UserContext)

  const { type } = useParams() ?? { type: 'mobile-list'}
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { formatFunction } = useFormat()

  const [customMode, setCustomMode] = useState<boolean>(false)
  const [data, setData] = useState<any>()
  const [rows, setRows] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  
  //Filters
  const [mobiles, setMobiles] = useState<number[]>([])
  const [fleets, setFleets] = useState<string[]>([])
  const [dates, setDates] = useState<DaterangeValue>()
  const [date, setDate] = useState<string | null>()
  const [filetype, setFiletype] = useState<string>(DEFAULT_FILE_TYPE)
  const [visited, setVisited] = useState<boolean>(false)
  const [speedOver, setSpeedOver] = useState<number>(DEFAULT_SPEED_OVER)
  const [weekrange, setWeekRange] = useState<string>(DEFAULT_WEEK_RANGE)
  const [areas, setAreas] = useState<number[]>([])

  const { data: fleetList } = useQuery(['getFleets'], getFleets)
  const { data: fullMobileList } = useQuery([], getMobileList)

  const mobileOptions = useMemo(() => {
    const opt: Record<number, string> = {}
    if (fullMobileList) {
      fullMobileList?.forEach((mobile) => {
        opt[mobile.id] = mobile.globalid
      })
    }
    return opt
  }, [fullMobileList])

  useEffect(() => {
    if (!type) {
      navigate('mobile-list')
    }
  }, [])

  const fleetMobileLookup = useMemo(() => {
    const lookup: Record<string, number[]> = {}
    fleetList?.forEach((fleet) => {
      lookup[fleet.fleetid] = fleet.mobiles.map((mobile) => mobile.id)
    })
    return lookup
  }, [fleetList])

  useEffect(() => {
    if (fleets?.length && fleetList) {
      const mobileIds = fleets.reduce((arr, fleet) => [...arr, ...fleetMobileLookup[fleet]], [])
      setMobiles(mobileIds)
    }
  }, [fleets])

  const clearFilters = useCallback(() => {
    setMobiles([])
    setFleets([])
    setDates({ from: DateTime.now().toISODate(), to: DateTime.now().toISODate()})
    setDate(DateTime.now().toISODate())
    setFiletype(DEFAULT_FILE_TYPE)
    setVisited(false)
    setSpeedOver(DEFAULT_SPEED_OVER)
    setWeekRange(DEFAULT_WEEK_RANGE)
    setAreas([])
  }, [])

  useEffect(() => {
    setData(undefined)
    clearFilters()
    updateSidebar(initial_sidebar)
  }, [type])

  const selectedReport = useMemo(() => {
    const paramType = type ?? 'mobile-list'
    if (!REPORTS[paramType]) return { ...REPORTS['mobile-list'], id: 'mobile-list'}
    return { ...REPORTS[paramType], id: paramType }
  }, [type])

  // Control states through search params (only on first render of the view)
  useEffect(() => {
    const { filters } = selectedReport
    const paramDate = searchParams.get('date')
    if (filters.includes('date') && paramDate !== date && isValue(paramDate)) setDate(paramDate)
    
    const paramFrom = searchParams.get('from')
    const paramTo = searchParams.get('to')
    if (
      filters?.includes('daterange') &&
      (paramFrom !== dates?.from || paramTo !== dates?.to) &&
      isValue(paramFrom) &&
      isValue(paramTo)
    ) {
      setDates({
        from: paramFrom ?? DateTime.now().toISODate(),
        to: paramTo ?? DateTime.now().toISODate()
      })
    }

    const paramFleet = searchParams.getAll('fleet')
    if (filters.includes('fleet') && paramFleet?.length) setFleets(paramFleet)
    
    const paramMobiles = searchParams.getAll('mobile')
    if (filters.includes('mobiles') && paramMobiles?.length) setMobiles(paramMobiles)

    const paramAreas = searchParams.getAll('areaids')
    if (filters.includes('areas') && paramAreas?.length) setAreas(paramAreas)
      
    tableRef?.current?.setSort({ column: selectedReport.columns[0]?.id, type: 'ascending' })
  }, [selectedReport])

  // Change params when the state changes
  useEffect(() => {
    const { filters } = selectedReport
    const newSearchParams = new URLSearchParams()

    if (filters.includes('date')) {
      newSearchParams.append('date', DateTime.fromISO(date).toISODate())
    } else if (filters.includes('daterange')) {
      newSearchParams.append('from', DateTime.fromISO(dates?.from).toISODate())
      newSearchParams.append('to', DateTime.fromISO(dates?.to).toISODate())
    }

    if (filters.includes('mobiles')) {
      mobiles?.forEach((mobile) => {
        newSearchParams.append('mobile', mobile.toString())
      })
    }

    if (filters.includes('fleets')) {
      fleets?.forEach((fleet) => {
        newSearchParams.append('fleet', fleet)
      })
    }

    if (filters.includes('areas')) {
      areas?.forEach((area) => {
        newSearchParams.append('areaids', area.toString())
      })
    }

    setSearchParams(newSearchParams)
  }, [date, dates, mobiles, fleets, areas])

  const changeRow = useCallback((rowId: number, changes: Record<string, any>) => {
    if (rowId) {
      setRows((prevRows) => prevRows.map((row) => {
        if (row._rowId === rowId) return { ...row, ...changes }
        return row
      }))
    }
  }, [rows])

  const getDynamicLabel = useCallback((fromValue: string, toValue?: string) => {
    if (!dynamicType) return `${DateTime.fromISO(fromValue).toFormat('LLLL')}`
    if (dynamicType === 'month') {
      const from = DateTime.fromISO(fromValue).toFormat('dd.LL.yyyy')
      const to = toValue ? DateTime.fromISO(toValue).toFormat('dd.LL.yyyy') : from
      return fromValue === toValue ? from : `${from} - ${to}`
    }

    if (dynamicType === 'week') return DateTime.fromISO(fromValue).toFormat('EEE dd.LL.yyyy')
    return null
  }, [dynamicType, t])

  const mappedHeader = useMemo(() => {
    const lookup = {}
    if (selectedReport.columns) {
      selectedReport.columns.forEach((col, idx) => {
        const { id, type: colType, isDynamic } = col
        if (isDynamic && data) {
          let { arrayPath, period, dataPath } = DYNAMIC_DATA[type] ?? {}
          if (arrayPath) {
            const dataArr = data?.[0] ?? {}
            const typeData = DYNAMIC_DATA[type][dynamicType]
            if (typeData?.period) period = typeData.period
            dataArr?.[arrayPath]?.forEach((val) => {
              const key = val[typeData?.period?.from] ?? val[period.from]
              lookup[key] = {
                ...col,
                index: col.index ?? idx,
                path: typeData?.datapath ?? dataPath,
                label: getDynamicLabel(val[period.from], val[period.to]),
                formatFunction: (val, row) => formatFunction(val, row, id, colType, {
                  changeRow,
                  headerId: key,
                  dynamicType,
                  format: typeData?.format ?? col.type,
                  isDynamic: true
                })
              }
            })
          }
        } else {
          lookup[id] = {
            ...col,
            index: col.index ?? idx,
            formatFunction: (val, row) => formatFunction(val, row, id, colType)
          }
        }
      })
    }
    return lookup
  }, [selectedReport, formatFunction, data, type, changeRow, dynamicType])

  const createRows = useCallback(() => {
    const rowArr = []
    if (data && selectedReport.hasTable) {
      data.forEach((d) => {
        const newRow = { ...d, _rowId: d[selectedReport.rowId], isExpanded: false }
        if (newRow.globalid && !newRow.mobileid) {
          newRow.mobileid = Object.keys(mobileOptions).find((id) => mobileOptions[id] === newRow.globalid)
        }
        Object.keys(mappedHeader).map((id) => {
          const { path, isDynamic } = mappedHeader[id]
          if (isDynamic) {
            let { dataPath, period, arrayPath } = DYNAMIC_DATA[type]
            if (DYNAMIC_DATA[type]?.[dynamicType]?.dataPath) dataPath = DYNAMIC_DATA[type]?.[dynamicType].dataPath
            if (DYNAMIC_DATA[type]?.[dynamicType]?.period) period = DYNAMIC_DATA[type]?.[dynamicType].period
            if (DYNAMIC_DATA[type]?.[dynamicType]?.arrayPath) arrayPath = DYNAMIC_DATA[type]?.[dynamicType].arrayPath
            d[arrayPath]?.forEach((val) => {
              const key = val[period.from]
              let value = newRow[key] ?? val[dataPath]
              if (key === id && dynamicType === 'week' && type === 'weekly-hours') {
                value = `${DateTime.fromISO(val.working_day_start).toFormat('HH:mm')} - ${DateTime.fromISO(val.working_day_end).toFormat('HH:mm')}`
              }
              newRow[key] = value
            })
          } else {
            const value = d[path]
            newRow[id] = value
          }
        })

        rowArr.push(newRow)
      })
    }
    setRows(rowArr)
  }, [mappedHeader, data, type, selectedReport, mobileOptions, dynamicType])

  useEffect(() => {
    createRows()
  }, [data])

  const fleetOptions = useMemo(() => {
    const opt: { [key: number]: string} = {}
    if (user) {
      user.available_fleets.forEach((fleet) => {
        opt[fleet.fleetid] = fleet.name
      })
    }
    return opt
  }, [user])

  const weekOptions = useMemo(() => {
    const opt: { [key: string]: string} = {}
    let day = DateTime.now().set({ weekday: 1 }).minus({ days: 7 })
    let weeks = 52
    while (weeks !== 0) {
      opt[day.toISODate()] = `W${day.weekNumber} ${day.toFormat('dd.LL.yyyy')}`
      day = day.minus({ days: 7})
      weeks = weeks - 1
    }

    return opt
  }, [])

  const funcParams = useMemo(() => {
    const params: Record<string, any> = {}
    if (selectedReport && selectedReport?.funcFilters) {
      Object.keys(selectedReport?.funcFilters).forEach((key) => {
        const filter = selectedReport.funcFilters?.[key]
        if (key === 'mobiles') {
          params[filter] = mobiles
        } else if (key === 'dates') {
          const { from, to } = filter
          params[from] = dates?.from ? DateTime.fromISO(dates?.from).toISODate() : dates?.from
          params[to] = dates?.to ? DateTime.fromISO(dates?.to).toISODate() : dates?.to
        } else if (key === 'filetype') {
          params[filter] = filetype
        } else if (key === 'visitedareas') {
          params[filter] = visited
        } else if (key === 'overlimit') {
          params[filter] = speedOver
        } else if (key === 'weekrange') {
          const { from, to } = filter
          const startDate = DateTime.fromISO(weekrange)
          const endDate = startDate.plus({ days: 7 })
          params[from] = startDate.toISODate()
          params[to] = endDate.toISODate()
        } else if (key === 'date') {
          const luxonDate = DateTime.fromISO(date)
          params[filter] = luxonDate.toISODate()
        } else if (key === 'areas') {
          params[filter] = areas
        }
      })
    }
    return { ...params, ...dynamicParams }
  }, [
    selectedReport.funcFilters, mobiles, dates, filetype, visited, speedOver, weekrange,
    date, dynamicParams, areas
  ])

  const getData = useCallback(async () => {
    setIsLoading(true)
    let respData = await selectedReport.getFunction(funcParams, dynamicType)
    if (selectedReport.id === 'fuel-purchases') {
      respData = respData.reduce((arr, val) => {
        const { globalid, purchases } = val
        purchases.forEach((purchase) => {
          arr.push({ ...purchase, globalid })
        })
        return arr
      }, [])
    } else if (selectedReport.id === 'temperature-quality-monitoring') {
      // Only 1 week allowed so the weeklist will only contain 1 obj in the array
      respData = respData.reduce((arr, val) => {
        const tempData = { ...val, ...val.weeklist[0] }
        delete tempData.weeklist
        arr.push(tempData)
        return arr
      }, [])
    }

    setData(respData)
  }, [selectedReport, funcParams, dynamicType])

  const getDataOnLoad = useCallback(async () => {
    await getData()
    setIsLoading(false)
  }, [getData])

  useEffect(() => {
    if (fetchOnLoad) {
      getDataOnLoad()
    }
  }, [selectedReport])

  const filters = useMemo(() => {
    const inputWidth = (1 / selectedReport?.filters?.length) * 100
    const style = {
      width: `${inputWidth}%`,
      minWidth: '160px',
      maxWidth: '400px'
    }
    return selectedReport.filters.map((key: string) => {
      if (key === 'fleet') {
        return (
          <div style={style}>
            <Input
              type="multiselect"
              size="m"
              label={t('Filter by fleet')}
              options={fleetOptions}
              value={fleets}
              onInput={(val) => setFleets(val)}
            />
          </div>
        )
      } else if (key === 'mobiles') {
        return (
          <div style={style}>
            <Input
              type="checkboxmultiselect"
              size="m"
              label={t('Mobiles')}
              options={mobileOptions}
              value={mobiles}
              onInput={(val) => setMobiles(val)}
            />
          </div>
        )
      } else if (key === 'daterange') {
        return (
          <div style={style}>
            <Input
              type="daterange"
              size="m"
              hideLabel={false}
              value={dates}
              onInput={(val) => setDates(val)}
            />
          </div>
        )
      } else if (key === 'filetype') {
        return (
          <div style={style}>
            <Input
              type="select"
              size="m"
              label={t('Filetype')}
              value={filetype}
              options={{ 'both': t('Both'), 'mobile': t('Mobile'), 'driver': t('Driver')}}
              onInput={(val) => setFiletype(val)}
            />
          </div>
        )
      } else if (key === 'visitedareas') {
        return (
          <div style={style}>
            <Input
              type="checkbox"
              size="m"
              label={t('Filter out visited areas')}
              value={visited}
              onInput={(val) => setVisited(!visited)}
            />
          </div>
        )
      } else if (key === 'overlimit') {
        return (
          <div style={style}>
            <Input
              type="select"
              size="m"
              label={t('Minimum speed over limit')}
              value={speedOver}
              options={{ 5: '5', 10: '10', 15: '15', 20: '20', 25: '25', 30: '30', 35: '35', 40: '40', 45: '45' }}
              onInput={(val) => setSpeedOver(val)}
            />
          </div>
        )
      } else if (key === 'weekrange') {
        return (
          <div style={style}>
            <Input
              type="select"
              size="m"
              label={t('Week')}
              value={weekrange}
              options={weekOptions}
              onInput={(val) => setWeekRange(val)}
            />
          </div>
        )
      } else if (key === 'date') {
        return (
          <div style={style}>
            <Input
              type="date"
              size="m"
              label={t('Date')}
              value={date}
              onInput={(val) => setDate(val)}
            />
          </div>
        )
      }
      return null
    })
  }, [
    selectedReport, fleetOptions, mobileOptions, mobiles, fleets, dates, filetype, visited, speedOver,
    weekOptions, weekrange, t, date,
  ])

  const submitButtonLabel = useMemo(() => {
    if (isLoading) {
      return (
        <>
          {`${t('Loading')}...`}
          <LoadingSpinner color={COLOR.WHITE} />
        </>
      )
    }

    return t('Submit')
  }, [isLoading, t])

  const filterSection = useMemo(() => {
    return (
      <>
        <div className="Reports__filters-inputs">
          {filters}
        </div>
        <div className="Reports__filters-buttons">
          <Button
            type="tertiary"
            size="m"
            label={t('Clear filters')}
            onClick={() => {}}
          />
          <Button
            type="primary"
            size="m"
            label={submitButtonLabel}
            onClick={() => {
              setData(undefined)
              getData()
            }}
          />
        </div>
      </>
    )
  }, [filters, isLoading, getData, submitButtonLabel])

  const customButtonLabel = useMemo(() => {
    const label = customMode ? t('Customise view is ON') : t('Customise view')
    return (
      <>
        {label}
        <Icon icon={ICON.MOVE} color={customMode ? COLOR.WHITE : COLOR.TEXT_GRAY} />
      </>
    )
  }, [t, customMode])

  const { options: chartOptions } = useHighcharts({
    title: selectedReport.graph?.title,
    subtitle: '',
    height: 400,
    axis: {
      xType: selectedReport.graph?.axis?.x?.type,
      yAxisTitle: selectedReport.graph?.axis?.y?.title
    },
    tooltip: selectedReport.graph?.tooltip,
  })

  const series = useMemo(() => {
    const { hasGraph, graph, seriesPath } = selectedReport ?? {}
    if (hasGraph) {
      const { sortby, dataValue, subtitle, min, max } = graph
      return data?.reduce((obj, val) => {
        const range = {
          name: 'range',
          data: [],
          type: 'arearange',
          lineWidth: 0,
          fillOpacity: 0.3,
          zIndex: 0,
          linkedTo: ':previous',
          marker: {
            enabled: false
          }
        }
        if (!val[seriesPath]?.length) return obj

        obj[val.id] = [{
          name: val[subtitle],
          zIndex: 1,
          color: COLOR.BLUE_70,
          data: val[seriesPath]
            ?.sort((a, b) => a[sortby] > b[sortby] ? 1 : -1)
            ?.map((d) => {
              if (min && max) {
                const value = [d[sortby], val[min], val[max]]
                range.data.push(value)
              }
              return [d[sortby], parseFloat(d[dataValue].toFixed(1))]
            })
        }]

        if (min && max) obj[val.id].push(range)
        return obj
      }, {})
    }
    return undefined
  }, [data, selectedReport])

  useEffect(() => {
    if (isLoading && data) {
      setIsLoading(false)
    }
  }, [rows, series])

  const graphs = useMemo(() => {
    const { hasGraph, graph, seriesPath } = selectedReport
    if (!hasGraph || isLoading) return null
    if (!Object.keys(series ?? {})?.length && data) {
      return (
        <div className="Reports__data-empty">
          {t('No data available')}
        </div>
      )
    }

    return (
      <>
        {data?.map((d, idx) => {
          const modOptions = {
            ...chartOptions,
            series: series?.[d.id] ?? []
          }

          if (graph.subtitle) {
            modOptions.subtitle = { text: d[graph.subtitle] }
          }

          if (d?.[seriesPath]?.length) {
            return (
              <>
                {idx === 0 && (
                  <Infobox
                    status='info'
                    label={t('Tip')}
                    closable={true}
                    message={t('If you want to zoom in a specific section of the graph, click and drag from left to right')}
                  />
                )}
                <HighchartsReact
                  highcharts={Highcharts}
                  options={modOptions}
                />  
              </>
            )
          }
          return null
        })}
      </>
    )
  }, [selectedReport, isLoading, series, t, chartOptions, data])

  return (
    <div className="Reports">
      {showHeader && (
        <>
          <div className="Reports__header">
            <h1>{t('Reports')}</h1>
            <Button
              size='s'
              type={customMode ? 'secondary' : 'tertiary'}
              label={customButtonLabel}
              onClick={() => setCustomMode(!customMode)}
            />
          </div>
          <div className="Reports__navbar">
            <ReportNavbar />
          </div>
          <div className="Reports__filters">
            {filterSection}
          </div>
        </>
      )}
      <div className="Reports__data">
        {graphs}
        {selectedReport.hasTable && (
          <Table
            ref={tableRef}
            isLoading={isLoading}
            title={tableTitle ?? selectedReport.name}
            showSearch={!!!dynamicType}
            tableHeader={mappedHeader}
            rows={rows}
            isExpandable={selectedReport.hasExpandRow && dynamicType !== 'week'}
            autoExpand={false}
            emptyTableContent={t('No data to show')}
            includePageFooter={!!!dynamicType}
          />
        )}
      </div>
    </div>
  )
})

export default Reports
